/**
 * Recovery Key Storage Utilities
 *
 * Encrypts/decrypts the user's SSSS recovery key for caching in
 * sessionStorage between page loads. NOTE: this layer is compliance
 * posture only — `sessionSecret` is stored next to the ciphertext in
 * the same sessionStorage, so an attacker with sessionStorage read
 * access has both. Treat this as obfuscation against shoulder-surfing
 * dev tools, NOT as defense-in-depth against script-level access.
 * The actual A_25616-relevant key derivation is the SSSS layer (see
 * src/crypto/utils/ssssAlgorithms.js and matrix-js-sdk's
 * RECOVERY_KEY_DERIVATION_ITERATIONS).
 *
 * Used by both ChatBackupPage and RecoveryKeyModal.
 */

import logger from './logger';

// Storage key helper for sessionStorage
const getStorageKey = (userId) => `ti_m_recovery_key_${userId}`;
const SESSION_SECRET_KEY = 'ti_m_recovery_session_secret';

// Encryption constants
const ENCRYPTION_ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const PBKDF2_ITERATIONS = 600000;
const SALT_LENGTH = 16; // 128-bit random salt for PBKDF2, stored alongside ciphertext

/**
 * Check if Web Crypto API is available
 */
const isCryptoAvailable = () => {
  const cryptoObj = window.crypto || window.msCrypto;
  return cryptoObj && cryptoObj.subtle;
};

/**
 * Get or create session-specific random secret
 * This adds a random component to the encryption key derivation
 * @returns {string} - Base64-encoded random secret
 */
const getOrCreateSessionSecret = () => {
  if (!isCryptoAvailable()) {
    throw new Error('Web Crypto API is not available in this browser');
  }

  let secret = sessionStorage.getItem(SESSION_SECRET_KEY);
  if (!secret) {
    const cryptoObj = window.crypto || window.msCrypto;
    const randomBytes = cryptoObj.getRandomValues(new Uint8Array(32));
    secret = btoa(String.fromCharCode(...randomBytes));
    sessionStorage.setItem(SESSION_SECRET_KEY, secret);
  }
  return secret;
};

/**
 * Derive an AES key from the user's mxid combined with a session secret using PBKDF2
 * @param {string} mxid - The user's Matrix ID
 * @param {Uint8Array} salt - Random salt bytes generated per encryption
 * @returns {Promise<CryptoKey>} - The derived AES key
 */
const deriveEncryptionKey = async (mxid, salt) => {
  if (!isCryptoAvailable()) {
    throw new Error('Web Crypto API is not available for encryption');
  }

  const cryptoObj = window.crypto || window.msCrypto;
  const sessionSecret = getOrCreateSessionSecret();
  const combinedMaterial = mxid + sessionSecret;

  const encoder = new TextEncoder();
  const keyMaterial = await cryptoObj.subtle.importKey(
    'raw',
    encoder.encode(combinedMaterial),
    'PBKDF2',
    false,
    ['deriveKey'],
  );

  return cryptoObj.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-512',
    },
    keyMaterial,
    { name: ENCRYPTION_ALGORITHM, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt'],
  );
};

/**
 * Encrypt recovery key using AES-GCM with mxid-derived key
 * @param {string} recoveryKey - The plain text recovery key
 * @param {string} mxid - The user's Matrix ID
 * @returns {Promise<string>} - Base64-encoded encrypted data (IV + ciphertext)
 */
export const encryptRecoveryKey = async (recoveryKey, mxid) => {
  if (!isCryptoAvailable()) {
    throw new Error('Web Crypto API is not available for encryption');
  }

  const cryptoObj = window.crypto || window.msCrypto;
  const salt = cryptoObj.getRandomValues(new Uint8Array(SALT_LENGTH)); // 128-bit random PBKDF2 salt
  const key = await deriveEncryptionKey(mxid, salt);
  const encoder = new TextEncoder();
  const iv = cryptoObj.getRandomValues(new Uint8Array(12)); // 96-bit IV for GCM

  const ciphertext = await cryptoObj.subtle.encrypt(
    { name: ENCRYPTION_ALGORITHM, iv },
    key,
    encoder.encode(recoveryKey),
  );

  // Combine salt + IV + ciphertext, then base64 encode
  const combined = new Uint8Array(salt.length + iv.length + ciphertext.byteLength);
  combined.set(salt);
  combined.set(iv, salt.length);
  combined.set(new Uint8Array(ciphertext), salt.length + iv.length);

  return btoa(String.fromCharCode(...combined));
};

/**
 * Decrypt recovery key using AES-GCM with mxid-derived key
 * @param {string} encryptedData - Base64-encoded encrypted data (IV + ciphertext)
 * @param {string} mxid - The user's Matrix ID
 * @returns {Promise<string>} - The decrypted recovery key
 */
export const decryptRecoveryKey = async (encryptedData, mxid) => {
  if (!isCryptoAvailable()) {
    throw new Error('Web Crypto API is not available for decryption');
  }

  const cryptoObj = window.crypto || window.msCrypto;
  const decoder = new TextDecoder();

  // Decode base64 and split salt + IV + ciphertext
  const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
  const salt = combined.slice(0, SALT_LENGTH);
  const iv = combined.slice(SALT_LENGTH, SALT_LENGTH + 12);
  const ciphertext = combined.slice(SALT_LENGTH + 12);

  const key = await deriveEncryptionKey(mxid, salt);

  const plaintext = await cryptoObj.subtle.decrypt(
    { name: ENCRYPTION_ALGORITHM, iv },
    key,
    ciphertext,
  );

  return decoder.decode(plaintext);
};

/**
 * Store encrypted recovery key in sessionStorage
 * @param {string} recoveryKey - The plain text recovery key
 * @param {string} userId - The user's Matrix ID
 * @returns {Promise<boolean>} - True if successful, false otherwise
 */
export const storeRecoveryKeyInSession = async (recoveryKey, userId) => {
  try {
    const encryptedKey = await encryptRecoveryKey(recoveryKey, userId);
    sessionStorage.setItem(getStorageKey(userId), encryptedKey);
    logger.debug('[RecoveryKeyStorage] Recovery key stored in sessionStorage');
    return true;
  } catch (err) {
    logger.error('[RecoveryKeyStorage] Failed to store recovery key:', err);
    return false;
  }
};

/**
 * Load and decrypt recovery key from sessionStorage
 * @param {string} userId - The user's Matrix ID
 * @returns {Promise<string|null>} - The decrypted recovery key or null if not found/failed
 */
export const loadRecoveryKeyFromSession = async (userId) => {
  try {
    const encryptedKey = sessionStorage.getItem(getStorageKey(userId));
    if (!encryptedKey) {
      return null;
    }
    const decryptedKey = await decryptRecoveryKey(encryptedKey, userId);
    logger.debug('[RecoveryKeyStorage] Recovery key loaded from sessionStorage');
    return decryptedKey;
  } catch (err) {
    logger.error('[RecoveryKeyStorage] Failed to load recovery key:', err);
    // Remove corrupted data
    sessionStorage.removeItem(getStorageKey(userId));
    return null;
  }
};

/**
 * Remove recovery key from sessionStorage
 * @param {string} userId - The user's Matrix ID
 */
export const removeRecoveryKeyFromSession = (userId) => {
  sessionStorage.removeItem(getStorageKey(userId));
  logger.debug('[RecoveryKeyStorage] Recovery key removed from sessionStorage');
};

/**
 * Check if a recovery key is stored in sessionStorage
 * @param {string} userId - The user's Matrix ID
 * @returns {boolean} - True if a key is stored
 */
export const hasStoredRecoveryKey = (userId) => {
  return !!sessionStorage.getItem(getStorageKey(userId));
};

// Export storage key function for direct sessionStorage access if needed
export { getStorageKey };
